import { useState } from "react";
import { ConfirmButton, primary, outline, danger } from "./ConfirmButton";
import { fmtDate } from "./StatusBadge";

const inputCls = "h-10 rounded-lg border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]";

const Block = ({ title, sub, children, testId }) => (
  <div className="rounded-xl border p-4" data-testid={testId}>
    <p className="text-sm font-semibold text-foreground">{title}</p>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    <div className="mt-3 flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

export const CompBlock = ({ a, run, busy }) => {
  const [until, setUntil] = useState("");
  const comped = a.status === "comped";
  return (
    <Block title="Complimentary access" testId="admin-comp-block"
      sub={comped ? `Comped ${a.comped_until ? `until ${fmtDate(a.comped_until)}` : "permanently"}. Stripe can't change this status.` : "Works like an active subscription. Leave the date empty for permanent."}>
      <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className={inputCls} data-testid="admin-comp-until-input" aria-label="Comp end date (optional)" />
      <button type="button" className={primary} disabled={busy} onClick={() => run("comp", { until: until || null })} data-testid="admin-comp-button">
        {comped ? "Update comp" : "Grant comp"}
      </button>
      {comped && (
        <ConfirmButton className={outline} disabled={busy} testId="admin-uncomp-button" title="Remove complimentary access?" confirm="Remove comp"
          description="The account goes back to its Stripe status. With no subscription it becomes canceled (or trial if the trial is still running) and the public link may turn off."
          onConfirm={() => run("uncomp")}>Remove comp</ConfirmButton>
      )}
    </Block>
  );
};

export const TrialBlock = ({ a, run, busy }) => {
  const [days, setDays] = useState(7);
  return (
    <Block title="Extend trial" sub={`Current trial end: ${fmtDate(a.trial_ends_at)}`} testId="admin-trial-block">
      <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} className={`${inputCls} w-24`} data-testid="admin-trial-days-input" aria-label="Days" />
      <button type="button" className={primary} disabled={busy || !(days >= 1 && days <= 365)} onClick={() => run("extend-trial", { days: Number(days) })} data-testid="admin-extend-trial-button">
        Add days
      </button>
    </Block>
  );
};

export const EmailBlock = ({ a, run, busy }) => (
  <Block title="Email" sub={a.email_verified ? "Email is verified." : "Email not verified yet: public link and trial are on hold."} testId="admin-email-block">
    {!a.email_verified && <>
      <button type="button" className={primary} disabled={busy} onClick={() => run("verify-email")} data-testid="admin-verify-email-button">Mark as verified</button>
      <button type="button" className={outline} disabled={busy} onClick={() => run("resend-verification")} data-testid="admin-resend-verification-button">Resend verification email</button>
    </>}
  </Block>
);

export const SuspendBlock = ({ a, run, busy }) => (
  <Block title="Account access" testId="admin-suspend-block"
    sub={a.is_admin ? "Admin accounts can't be suspended." : a.suspended ? "Suspended: login blocked and public link off." : "Suspending blocks login and turns the public link off."}>
    {!a.is_admin && (a.suspended
      ? <button type="button" className={primary} disabled={busy} onClick={() => run("unsuspend")} data-testid="admin-unsuspend-button">Reactivate account</button>
      : <ConfirmButton className={danger} disabled={busy} testId="admin-suspend-button" title={`Suspend ${a.company_name}?`} confirm="Suspend"
          description="They will be logged out, can't log in, and their public quote link turns off until you reactivate them." onConfirm={() => run("suspend")}>Suspend account</ConfirmButton>)}
  </Block>
);

export const PreviewLimitBlock = ({ a, run, busy }) => {
  const [limit, setLimit] = useState(a.preview_limit ?? "");
  return (
    <Block title="Monthly preview limit" sub={`${a.previews_used} of ${a.preview_limit_effective} used this month${a.preview_limit === null ? " (default)" : ""}`} testId="admin-preview-limit-block">
      <input type="number" min={0} max={1000} value={limit} placeholder="30" onChange={(e) => setLimit(e.target.value)} className={`${inputCls} w-24`} data-testid="admin-preview-limit-input" aria-label="Preview limit" />
      <button type="button" className={primary} disabled={busy || limit === "" || limit < 0 || limit > 1000} onClick={() => run("preview-limit", { limit: Number(limit) }, "put")} data-testid="admin-preview-limit-save">Save</button>
      {a.preview_limit !== null && <button type="button" className={outline} disabled={busy} onClick={() => { setLimit(""); run("preview-limit", { limit: null }, "put"); }} data-testid="admin-preview-limit-reset">Use default</button>}
    </Block>
  );
};
